"use client";
import { useAtom } from "jotai";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth, useI18N, useSearchPageParams } from "common/hooks";
import { bucketUrlPrefix } from "common/config";
import { useDarkMode } from "@/hooks/useDarkMode";
import "react-tooltip/dist/react-tooltip.css";
import {
  drawerMenuOpenAtom,
  onboardingCurrentScreenAtom,
  onboardingScreenAtom,
} from "@/store/atoms";
import { Sheet, SheetContent, SheetTitle } from "@/shadcn/components/ui/sheet";
import GetNavigationPages from "./GetNavigationPages";
import { useResetOnboarding } from "@/features/onboarding";
import { useOpenLearningHub } from "@/features/learning-hub";
import { useCallback, useEffect, useRef, useState } from "react";
import ModalWithAction from "@/components/common/ModalWithAction";
import { ArrowSquareOutIcon } from "@phosphor-icons/react";
import OnboardingScreensComponent from "../auth/modals/Components/OnboardingScreensComponent";
import { trackEvent, createPageAnalytics, resetAnalytics, registerSuperProperties } from "common/analytics";
import { resolveDeviceFormFactor } from "@/analytics/device";
import { entryPageFromCurrentPage } from "@/analytics/entryPage";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";

function HamburgerMenu() {
  const i18n = useI18N();
  const isDark = useDarkMode();
  const navigate = useNavigate({ from: "/" });
  const { logout, user } = useAuth();
  const profileUrl = bucketUrlPrefix + (user as any)?.profile_photo;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setIsOnboardingModalOpen] = useAtom(onboardingScreenAtom);
  const [, setOnboardingCurrentScreen] = useAtom(onboardingCurrentScreenAtom);
  const { showModal } = useAuthModals();

  // Global search params for syncing with BrandsSection navigation
  const { setSearchPageParams } = useSearchPageParams();

  // Assuming`user` is defined somewhere in your scope
  const isLoggedIn = !!user?.name; // This checks if user is logged in
  const openLearningHub = useOpenLearningHub();

  /*
   * Developer-only "Reset onboarding" row - the group is only built on
   * non-production environments, so this is inert everywhere else.
   *
   * Confirmed before it runs, like logging out below: it throws away answers
   * the user cannot get back without walking the flow again. The drawer stays
   * open behind the confirmation; committing reloads the page, which takes the
   * drawer with it.
   */
  const resetOnboarding = useResetOnboarding();
  const [isResetOnboardingOpen, setIsResetOnboardingOpen] = useState(false);

  const handleResetOnboarding = () => {
    resetOnboarding();
    // Reload so the flow starts the way a first visit does, from a clean page
    // rather than over whatever this session has open. The reset's writes to
    // localStorage are synchronous, so they are all in place by now.
    window.location.reload();
  };

  const pages = GetNavigationPages(
    isLoggedIn,
    setIsModalOpen,
    setIsOnboardingModalOpen,
    setOnboardingCurrentScreen,
    () => setIsResetOnboardingOpen(true),
  );

  const [drawerMenuOpen, setDrawerMenuOpen] = useAtom(drawerMenuOpenAtom);
  const [liveMessage, setLiveMessage] = useState("");

  // Reset isModalOpen when drawerMenuOpen closes or user logs out
  useEffect(() => {
    if (!drawerMenuOpen) {
      console.log("Drawer closed - resetting isModalOpen to false");
      setIsModalOpen(false);
    }
  }, [drawerMenuOpen]);

  // Debug logging for isModalOpen changes
  useEffect(() => {
    console.log("isModalOpen changed to:", isModalOpen);
  }, [isModalOpen]);

  useEffect(() => {
    if (!user) {
      setIsModalOpen(false);
    }
  }, [user]);

  const toggleDrawerMenu = useCallback(() => {
    setDrawerMenuOpen((drawerMenuOpen) => !drawerMenuOpen);
  }, [setDrawerMenuOpen]);

  const handleHamburgerLogout = useCallback(async () => {
    console.log("handleHamburgerLogout called!");

    // Outcome event — fire while identity is still attached, before resetAnalytics() clears it.
    trackEvent("account_logged_out", {});

    // New centralized analytics: clear identity + super properties on logout, then re-establish
    // the device-level baseline (reset() also wipes product_type/device/dark_mode). auth_state/
    // user_type are re-registered by useAnalyticsSuperProperties once `user` clears.
    resetAnalytics();
    registerSuperProperties({
      product_type: "app",
      device: resolveDeviceFormFactor(),
      dark_mode: !!isDark,
    });

    setIsModalOpen(false);
    toggleDrawerMenu();
    logout();

    //searhc page param tagging_mode to "off"
    setSearchPageParams((prev) => ({
      ...prev,
      tagging_mode: "off",
    }));


    navigate({ to: "/" });
  }, [logout, navigate, toggleDrawerMenu, isDark]);

  const firstFocusHamburgerRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (drawerMenuOpen && firstFocusHamburgerRef.current) {
      console.log("drawerMenuOpen", drawerMenuOpen);
      // Delay focusing to ensure the element is rendered (reduced from 1000ms for better UX)
      setTimeout(() => {
        console.log("drawerMenuOpen focus set");

        firstFocusHamburgerRef.current.focus();
      }, 100);
    }
  }, [drawerMenuOpen]); // Trigger the effect when the drawer menu opens

  // Handle Escape key press to close the drawer
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && drawerMenuOpen) {
        setDrawerMenuOpen(false);
        setLiveMessage("Menu closed");
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [drawerMenuOpen, setDrawerMenuOpen]);

  const handleOpenAuthModal = () => {
    trackEvent("auth_form_opened", {
      entry_point: "hamburger_menu",
      entry_page: entryPageFromCurrentPage(),
    });
    showModal(AuthModalScreen.EnterEmail);
  };

  return (
    <>
      <Sheet
        open={drawerMenuOpen}
        onOpenChange={() => {
          // toggleDrawerMenu();
          setLiveMessage("Menu closed");
        }}
      >
        <SheetContent
          showClose={false}
          side="right"
          className="w-full bg-bg-brand-secondary-default p-4 flex flex-col h-screen overflow-y-auto"
          aria-label="Navigation drawer"
        >
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <div>
            <div className="relative flex items-center py-6">
              <button
                type="button"
                onClick={() => {
                  createPageAnalytics("profile_menu").track("close_action_button", {});
                  toggleDrawerMenu();
                }}
                className="absolute left-0 focus-blue cursor-pointer bg-transparent border-0 p-0"
                aria-label="Close menu"
              >
                {isDark ? (
                  <img
                    src="/images/Close_dark.svg"
                    alt=""
                    aria-hidden="true"
                  />
                ) : (
                  <img
                    src="/images/Close.svg"
                    alt=""
                    aria-hidden="true"
                  />
                )}
              </button>

              <span className="title-s-semibold text-fg-neutral-strong w-full text-center">
                {i18n.get("profile")}
              </span>
            </div>

            {/* Settings Menu Content */}
            <main className="flex flex-col gap-4 bg-bg-brand-secondary-default w-full">
              {!user?.name && (
                <>
                  {/* Signed out - native's `ProfileGuestBanner`, word for
                      word (design QA: same copy and actions as RN): what an
                      account is for, "Create account", and a way to see what
                      Sociability does without one. */}
                  <section
                    className="relative flex flex-col gap-4 overflow-hidden px-4 py-6 bg-container-brand-minimal"
                    aria-labelledby="guest-profile-banner-title"
                  >
                    <div className="flex flex-col gap-1">
                      <h2
                        id="guest-profile-banner-title"
                        className="title-s-semibold text-fg-neutral-strong"
                      >
                        {i18n.get("guestProfileBannerTitle")}
                      </h2>
                      <p className="body-m-medium text-fg-neutral-subtle">
                        {i18n.get("guestProfileBannerBody")}
                      </p>
                    </div>
                    {/* Native's order: the link, then the button, with
                        the illustration beside them at 120x111. */}
                    {/* Clear of the corner illustration by padding, not a
                        shared row - a row took the picture's height and
                        opened a gap above the link (design QA). */}
                    <div className="flex flex-col items-start gap-4 pr-[114px]">
                        <button
                          type="button"
                          className="min-h-6 rounded text-left label-m-medium text-fg-brand-subtle underline underline-offset-2 hover:text-fg-brand-strong focus-blue"
                          onClick={() => {
                            toggleDrawerMenu();
                            openLearningHub();
                          }}
                        >
                          {i18n.get("guestProfileBannerLink")}
                        </button>
                        <button
                          type="button"
                          className="flex items-center justify-center min-h-[40px] min-w-[44px] px-3 py-2 bg-bg-brand-primary-default rounded-lg focus-blue hover:bg-bg-brand-primary-hover transition-colors"
                          onClick={() => {
                            handleOpenAuthModal();
                            toggleDrawerMenu();
                          }}
                        >
                          <span className="label-m-medium text-fg-neutral-onFilled">
                            {i18n.get("createAccount")}
                          </span>
                        </button>
                    </div>
                    <img
                      src="/images/menu/profile-guest-banner.png"
                      alt=""
                      aria-hidden="true"
                      width={120}
                      height={111}
                      className="pointer-events-none absolute bottom-[23px] right-[2px]"
                    />
                  </section>
                </>
              )}

              {user?.name && (
                <section className="py-2">
                  <Link
                    to="/your-profile"
                    onClick={toggleDrawerMenu}
                    className="flex items-center gap-4 w-full min-h-[44px] px-4 py-2 bg-bg-brand-secondary-default hover:bg-bg-brand-ghost-hover transition-colors focus-blue"
                    aria-label={`Profile of ${user?.name}`}
                  >
                    {(user as any)?.profile_photo ? (
                      <img
                        className="w-10 h-10 rounded-full"
                        src={profileUrl}
                        alt={`Profile picture of ${user?.name}`}
                      />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-medium select-none"
                        style={{ backgroundColor: user?.profile_color_code ?? "#8751FF" }}
                        aria-label={`Profile initials of ${user?.name}`}
                      >
                        {user?.name?.charAt(0)?.toUpperCase() ?? ""}
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span
                        className="body-base-medium text-fg-neutral-strong"
                        ref={firstFocusHamburgerRef}
                      >
                        {user?.name}
                      </span>
                      <span className="body-s-regular text-fg-neutral-subtle">
                        {i18n.get("manageAccount") || "Manage account"}
                      </span>
                    </div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      className="ml-auto text-fg-neutral-subtle"
                      aria-hidden="true"
                    >
                      <path
                        d="M7.5 15L12.5 10L7.5 5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                </section>
              )}

              {pages.map(({ elements, group, groupToShowForType }, index) => {
                // CheckIcon if this section should be shown
                const shouldShowSection =
                  (groupToShowForType === "loggedin" && isLoggedIn) ||
                  groupToShowForType === "both";

                if (!shouldShowSection) return null;

                // Filter elements that should be shown
                const visibleElements = elements.filter(
                  ({ linkToShowForType }) =>
                    isLoggedIn
                      ? linkToShowForType === "loggedin" ||
                        linkToShowForType === "both"
                      : linkToShowForType === "both",
                );

                if (visibleElements.length === 0) return null;

                return (
                  <section
                    key={index}
                    className="flex flex-col bg-bg-brand-secondary-default"
                    aria-labelledby={`group-${index}`}
                  >
                    <h2
                      id={`group-${index}`}
                      className="body-base-medium text-fg-neutral-subtle px-4"
                    >
                      {group}
                    </h2>

                    <ul className="flex flex-col">
                      {visibleElements.map(
                        (
                          element,
                          elemIndex,
                        ) => {
                          const { icon, iconDark, name, path, onClick, publicUrl } =
                            element;
                          // Only the rows copied from native carry a glyph
                          // component instead of an SVG path.
                          const Icon = "Icon" in element ? element.Icon : undefined;
                          const linkIcon = isDark ? iconDark : icon;
                          const linkTarget = publicUrl ? "_blank" : "_self";
                          const linkPath = publicUrl || path;
                          const publicUrlIcon = publicUrl ? true : false;

                          return (
                            <li
                              key={name}
                              className={
                                elemIndex > 0
                                  ? "border-t border-neutral-100"
                                  : ""
                              }
                            >
                              <Link
                                className="flex items-center gap-4 w-full min-h-[44px] px-4 py-2 bg-bg-brand-secondary-default hover:bg-bg-brand-ghost-hover transition-colors focus-blue"
                                to={linkPath}
                                target={linkTarget}
                                onClick={onClick}
                                aria-label={
                                  publicUrlIcon
                                    ? `Navigate to ${name} (opens in new window)`
                                    : `Navigate to ${name}`
                                }
                              >
                                {/* Not every row has a glyph - the
                                    developer-only ones do not. An empty src
                                    renders a broken-image box, so hold the
                                    column open instead and keep the labels
                                    aligned. */}
                                {Icon ? (
                                  // Rows copied from native's menu use its
                                  // Phosphor glyphs; there is no SVG for them.
                                  <Icon
                                    size={24}
                                    aria-hidden="true"
                                    className="shrink-0 text-fg-neutral-strong"
                                  />
                                ) : linkIcon ? (
                                  <img
                                    src={linkIcon}
                                    alt=""
                                    width="24"
                                    height="24"
                                    aria-hidden="true"
                                  />
                                ) : (
                                  <span
                                    aria-hidden="true"
                                    className="h-6 w-6 shrink-0"
                                  />
                                )}
                                <span className="body-base-medium text-fg-neutral-strong">
                                  {name}
                                </span>
                                {publicUrlIcon && (
                                  <ArrowSquareOutIcon
                                    size={16}
                                    className="ml-auto text-fg-neutral-strong"
                                    aria-hidden="true"
                                  />
                                )}
                              </Link>
                            </li>
                          );
                        },
                      )}
                    </ul>
                  </section>
                );
              })}

              {/* Version Footer */}
              {/* <footer className="py-4">
 <p className="body-base-medium text-fg-neutral-subtle text-center">
 Version xx.xx.xxx
 </p>
 </footer> */}
            </main>

            {user?.name && (
              <div className="flex justify-center px-4 py-4">
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 w-full min-h-[40px] px-3 py-2 bg-bg-brand-primary-default rounded-lg focus-blue hover:bg-bg-brand-primary-hover transition-colors"
                  onClick={handleHamburgerLogout}
                >
                  <span className="body-base-medium text-white">Log out</span>
                </button>
              </div>
            )}
          </div>

          <ModalWithAction
            isModalOpen={isModalOpen}
            type="default"
            actionButtonType="default"
            title={i18n.get("logOut")}
            subtitle={i18n.get("areYouSureYouWishToLogOut")}
            cancelButtonTitle={i18n.get("cancel")}
            actionButtonTitle={i18n.get("logOut")}
            onClose={() => {
              console.log("onClose called");
              setIsModalOpen(false);
            }}
            onClick={handleHamburgerLogout}
            aria-label="Logout confirmation modal"
          />
        </SheetContent>
      </Sheet>

      {/*
        A sibling of <Sheet>, not a child of <SheetContent>.
        Radix unmounts the sheet's content when it closes, and anything portalled
        from inside it goes at the same time - so a confirmation living in there
        only survives for as long as the sheet does. It happens to survive today
        because the sheet's exit animation never completes and Radix leaves the
        content mounted, which is not a thing to depend on.
      */}
      <ModalWithAction
        isModalOpen={isResetOnboardingOpen}
        type="default"
        actionButtonType="default"
        title={i18n.get("resetOnboarding")}
        subtitle={i18n.get("resetOnboardingConfirmationDesktop")}
        cancelButtonTitle={i18n.get("cancel")}
        actionButtonTitle={i18n.get("reset")}
        onClose={() => setIsResetOnboardingOpen(false)}
        onClick={handleResetOnboarding}
        aria-label="Reset onboarding confirmation modal"
      />

      <div aria-live="assertive" className="sr-only">
        {liveMessage}
      </div>

      <OnboardingScreensComponent onClose={() => {}} />
    </>
  );
}

export default HamburgerMenu;
